'use client';

import React, { useState } from 'react';
import { Play, Pause, X, Volume2, VolumeX, Music2 } from 'lucide-react';
import { usePlayer } from '@/contexts/PlayerContext';

function formatTime(s: number): string {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export default function MiniPlayer() {
  const { currentTrack, isPlaying, progress, volume, duration, currentTime, togglePlay, seek, setVolume, stopPlayer, isSnippet } = usePlayer();
  const [showVolume, setShowVolume] = useState(false);

  if (!currentTrack) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 border-t"
      style={{
        background: 'rgba(13, 21, 32, 0.97)',
        backdropFilter: 'blur(16px)',
        borderColor: 'var(--border)',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.4)',
      }}
    >
      {/* Progress bar — full width, clickable */}
      <div
        className="w-full h-1 cursor-pointer group"
        style={{ background: 'var(--muted)' }}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const pct = ((e.clientX - rect.left) / rect.width) * 100;
          seek(Math.max(0, Math.min(100, pct)));
        }}
      >
        <div
          className="h-full transition-all duration-150"
          style={{
            width: `${progress}%`,
            background: 'linear-gradient(90deg, var(--secondary), var(--accent))',
          }}
        />
      </div>

      <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-4 py-2 sm:py-3 max-w-screen-2xl mx-auto">
        {/* Cover art */}
        <div
          className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
          style={{ background: currentTrack.coverGradient || 'linear-gradient(135deg, #1B2A4A, #2A9D8F)' }}
        >
          {currentTrack.coverUrl ? (
            <img src={currentTrack.coverUrl} alt={`${currentTrack.title} cover`} className="w-full h-full object-cover" />
          ) : (
            <Music2 size={16} className="text-white/60" />
          )}
        </div>

        {/* Track info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-xs sm:text-sm font-semibold truncate">{currentTrack.title}</p>
            {isSnippet && (
              <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 font-semibold hidden sm:inline"
                style={{ background: 'rgba(42,157,143,0.2)', color: 'var(--secondary)', fontSize: '10px' }}>
                PREVIEW
              </span>
            )}
          </div>
          <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>{currentTrack.artist}</p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Play/Pause */}
          <button
            onClick={togglePlay}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-105"
            style={{ background: 'linear-gradient(135deg, var(--secondary), var(--accent))' }}
          >
            {isPlaying ? <Pause size={16} className="text-white" /> : <Play size={16} className="text-white ml-0.5" />}
          </button>
        </div>

        {/* Time */}
        <div className="hidden sm:flex items-center gap-1 text-xs tabular-nums flex-shrink-0"
          style={{ color: 'var(--muted-foreground)' }}>
          <span>{formatTime(currentTime)}</span>
          <span>/</span>
          <span>{isSnippet ? '0:30' : formatTime(duration)}</span>
        </div>

        {/* Volume */}
        <div className="relative hidden md:flex items-center gap-2">
          <button
            onClick={() => setShowVolume(!showVolume)}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            style={{ color: 'var(--muted-foreground)' }}
          >
            {volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          {showVolume && (
            <div className="absolute bottom-10 right-0 p-3 rounded-xl border"
              style={{ background: 'var(--card)', borderColor: 'var(--border)', width: '140px' }}>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="w-full accent-teal-400"
                style={{ accentColor: 'var(--secondary)' }}
              />
              <p className="text-xs text-center mt-1" style={{ color: 'var(--muted-foreground)' }}>
                {Math.round(volume * 100)}%
              </p>
            </div>
          )}
        </div>

        {/* Close */}
        <button
          onClick={stopPlayer}
          className="p-1.5 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
